import {
    CHAT_LOAD_THREAD_SENDING,
    CHAT_LOAD_THREAD_SUCCESS,
    CHAT_LOAD_THREAD_FAILURE,
    CHAT_SEND_START,
    CHAT_THREAD_READY,
    CHAT_TOKEN,
    CHAT_TOOL_START,
    CHAT_TOOL_END,
    CHAT_STREAM_DONE,
    CHAT_STREAM_ERROR,
    CHAT_ABORTED,
    CHAT_NEW_THREAD,
    CHAT_CLEAR,
    CHAT_SET_AVAILABILITY,
} from '../constants/chatConstants';
import constants from '../../constants/constants';
import ChatServiceImpl from '../../service/ChatServiceImpl';
import AuthServiceImpl from '../../service/AuthServiceImpl';
import ToastUtil from '../../utils/ToastUtils';

const getToken = () => sessionStorage.getItem(constants.SESSION_STORAGE.ACCESS_TOKEN);

/**
 * Tokens minted before the server's getToken header fix carry no phoneNumber
 * claim, so the chat cannot tell who is asking. Rather than dead-ending an
 * already logged-in player, mint a fresh token from the stored phone number.
 */
const refreshAccessToken = async () => {
    const phoneNumber = sessionStorage.getItem(constants.SESSION_STORAGE.PLAYER_PHONE_NUMBER);
    if (!phoneNumber) return null;

    const response = await AuthServiceImpl.getAccessToken(phoneNumber);
    const token = response?.data?.access_token;
    if (token) sessionStorage.setItem(constants.SESSION_STORAGE.ACCESS_TOKEN, token);
    return token ?? null;
};

const isStaleToken = (error) => error?.code === 'AI_STALE_TOKEN' || error?.response?.data?.code === 'AI_STALE_TOKEN';

const messageForError = (error) => {
    const code = error?.code ?? error?.response?.data?.code;
    if (code === 'AI_RATE_LIMIT') return constants.CHAT.ERROR_RATE_LIMIT;
    // The server's message names the actual limit, so prefer it over the generic string.
    if (code === 'AI_DAILY_LIMIT') return error?.response?.data?.message ?? error?.message ?? constants.CHAT.ERROR_DAILY_LIMIT;
    if (code === 'AI_DISABLED' || code === 'AI_CONFIG') return constants.CHAT.ERROR_DISABLED;
    if (code === 'AI_STALE_TOKEN') return constants.CHAT.ERROR_STALE_TOKEN;
    if (code === 'AI_STREAM_TRUNCATED') return constants.CHAT.ERROR_TRUNCATED;
    if (error?.message === 'Failed to fetch') return constants.CHAT.ERROR_NETWORK;
    return error?.userMessage || constants.CHAT.ERROR_GENERAL;
};

export const loadThread = () => {
    return async (dispatch) => {
        dispatch({ type: CHAT_LOAD_THREAD_SENDING });
        try {
            let response;
            try {
                response = await ChatServiceImpl.getThread(getToken());
            } catch (error) {
                if (!isStaleToken(error)) throw error;
                const refreshed = await refreshAccessToken();
                if (!refreshed) throw error;
                response = await ChatServiceImpl.getThread(refreshed);
            }

            dispatch({
                type: CHAT_LOAD_THREAD_SUCCESS,
                threadId: response?.data?.threadId ?? null,
                messages: (response?.data?.messages ?? []).map((m) => ({
                    id: m.id,
                    role: m.role,
                    content: m.content,
                    status: 'done',
                })),
            });
        } catch (error) {
            const code = error?.response?.data?.code;
            if (code === 'AI_DISABLED') dispatch({ type: CHAT_SET_AVAILABILITY, isAvailable: false });
            dispatch({ type: CHAT_LOAD_THREAD_FAILURE, error: messageForError(error) });
        }
    };
};

export const startNewThread = () => {
    return async (dispatch) => {
        try {
            const response = await ChatServiceImpl.startNewThread(getToken());
            dispatch({ type: CHAT_NEW_THREAD, threadId: response?.data?.threadId ?? null });
        } catch (error) {
            ToastUtil.error(messageForError(error));
        }
    };
};

/**
 * Sends one message and streams the reply.
 *
 * `abortController` is owned by the component so the stop button and unmount can
 * both cancel it; aborting also stops the run server-side.
 */
export const sendMessage = (message, abortController) => {
    return async (dispatch, getState) => {
        const localId = `local-${Date.now()}`;
        dispatch({ type: CHAT_SEND_START, localId, message });

        const run = async (token) =>
            ChatServiceImpl.streamMessage({
                token,
                threadId: getState().chat.threadId,
                message,
                signal: abortController?.signal,
                onEvent: (name, data) => {
                    switch (name) {
                        case 'thread':
                            dispatch({ type: CHAT_THREAD_READY, threadId: data.threadId });
                            break;
                        case 'token':
                            dispatch({ type: CHAT_TOKEN, delta: data.delta });
                            break;
                        case 'tool_start':
                            dispatch({ type: CHAT_TOOL_START, toolName: data.toolName, label: data.label });
                            break;
                        case 'tool_end':
                            dispatch({ type: CHAT_TOOL_END });
                            break;
                        case 'done':
                            dispatch({ type: CHAT_STREAM_DONE, messageId: data.messageId, content: data.content });
                            break;
                        case 'error':
                            dispatch({ type: CHAT_STREAM_ERROR, error: data.message || constants.CHAT.ERROR_GENERAL });
                            break;
                        default:
                            break;
                    }
                },
            });

        try {
            try {
                await run(getToken());
            } catch (error) {
                if (!isStaleToken(error)) throw error;
                const refreshed = await refreshAccessToken();
                if (!refreshed) throw error;
                await run(refreshed);
            }
        } catch (error) {
            if (error?.name === 'AbortError') {
                dispatch({ type: CHAT_ABORTED });
                return;
            }
            const text = messageForError(error);
            dispatch({ type: CHAT_STREAM_ERROR, error: text });
            ToastUtil.error(text);
        }
    };
};

export const clearChat = () => ({ type: CHAT_CLEAR });
